import React, { useMemo } from "react"

import { RJSFSchema } from "shared/types/RJSF"

import { RJSFFormFieldArray } from "./RJSFFormFieldArray"
import { RJSFFormFieldFactory } from "./RJSFFormFieldFactory"
import { RJSFFormObjectFieldWrapperFactory } from "./RJSFFormObjectFieldWrapperFactory"
import { RJSFFormOneOfObjectField } from "./RJSFFormOneOfObjectField"
import { RJSFFormFieldProps } from "./types"

interface RJSFFormObjectFieldProps {
  path?: string
  schema: RJSFSchema
  isDisabled?: boolean

  FieldInformationRenderer?: React.ComponentType<RJSFFormFieldProps>
}

const childArrayPropsNames = ["anyOf", "allOf", "oneOf"] as const

export const RJSFFormObjectField = React.memo(
  ({
    path,
    schema,
    isDisabled,
    FieldInformationRenderer
  }: RJSFFormObjectFieldProps) => {
    const childArrayPropsName = childArrayPropsNames.find(
      propName => schema[propName]
    )
    const properties = useMemo(
      () => (schema.properties ? Object.entries(schema.properties) : null),
      [schema.properties]
    )
    const hasObjectChildren = useMemo(
      () =>
        properties?.some(([, value]) => value.type === "object" || !value.type),
      [properties]
    )

    if (childArrayPropsName === "oneOf") {
      return (
        <RJSFFormOneOfObjectField
          schema={schema}
          isDisabled={isDisabled}
          prefix={path}
          FieldInformationRenderer={FieldInformationRenderer}
        />
      )
    }

    if (childArrayPropsName) {
      return (
        <RJSFFormObjectFieldWrapperFactory
          schema={schema}
          childrenCount={schema[childArrayPropsName]?.length}
          hasObjectChildren
        >
          <RJSFFormFieldArray
            schemas={schema[childArrayPropsName] as RJSFSchema[]}
            isDisabled={isDisabled}
            prefix={path}
            FieldInformationRenderer={FieldInformationRenderer}
          />
        </RJSFFormObjectFieldWrapperFactory>
      )
    }

    if (!properties) {
      return null
    }

    return (
      <RJSFFormObjectFieldWrapperFactory
        schema={schema}
        childrenCount={properties?.length}
        hasObjectChildren={hasObjectChildren}
      >
        {properties.map(([key, value]) => (
          <RJSFFormFieldFactory
            key={key}
            path={path ? `${path}.${key}` : key}
            schema={value as RJSFSchema}
            isDisabled={isDisabled}
            FieldInformationRenderer={FieldInformationRenderer}
          />
        ))}
      </RJSFFormObjectFieldWrapperFactory>
    )
  }
)
